import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  RiTeamLine,
  RiGlobalLine,
  RiAwardLine,
  RiArrowRightLine,
  RiShieldCheckLine,
  RiRefreshLine,
  RiArrowUpLine,
} from 'react-icons/ri';

import { API_BASE } from '../config';
import { useSummit } from "../pages/summitdetails";

/* ─── THEME COLORS (Teal & Gold Palette) ───────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
};

/* ─── CSS (with comprehensive responsive rules) ──────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  /* HERO */
  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(40px,7vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(1.8rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(.85rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}

  /* STATS */
  .t-stats{display:flex;gap:20px;flex-wrap:wrap;margin-top:20px}
  .t-st{background:${T.white};border-radius:20px;padding:16px 28px;min-width:140px;text-align:center;border:1px solid ${T.border};box-shadow:${T.shadow}}
  .t-st-n{display:block;font-size:34px;font-weight:800;color:${T.accentGold};line-height:1.2}
  .t-st-l{display:block;font-size:12px;color:${T.textMuted};margin-top:6px;letter-spacing:.5px}

  /* MEMBERS GRID */
  .t-members-wrapper{padding:40px clamp(16px,4vw,64px) 56px;background:${T.offWhite}}
  .t-members-container{max-width:1320px;margin:0 auto}
  .t-members-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:24px;margin-bottom:56px}
  .t-member-card{background:${T.white};border-radius:22px;border:1px solid ${T.border};overflow:hidden;box-shadow:${T.shadow};transition:all .3s ease}
  .t-member-card:hover{transform:translateY(-8px);box-shadow:${T.shadowLg}}
  .t-member-header{display:flex;align-items:flex-start;gap:20px;padding:28px 28px 0}
  .t-member-avatar{width:72px;height:72px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:22px;font-weight:800;color:white;flex-shrink:0}
  .t-member-info{flex:1;min-width:0}
  .t-member-name{font-size:18.5px;font-weight:800;color:${T.text};margin-bottom:5px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
  .t-member-flag{font-size:18px}
  .t-member-institution{font-size:13.5px;font-weight:600;margin-bottom:10px}
  .t-member-field{display:inline-block;padding:4px 13px;border-radius:99px;font-size:11.5px;font-weight:700;letter-spacing:0.5px}
  .t-member-divider{height:1px;margin:18px 28px;background:linear-gradient(90deg,var(--member-color),transparent)}
  .t-member-bio{padding:0 28px 20px;font-size:14.5px;color:${T.textMid};line-height:1.75}
  .t-member-footer{height:3px;width:100%;border-radius:99px;margin-top:4px;opacity:0.6}

  /* COUNTRY BANNER */
  .t-country-banner{background:${T.white};border:1px solid ${T.borderLight};border-radius:22px;padding:clamp(28px,4vw,44px) clamp(24px,4vw,44px);margin-bottom:56px;box-shadow:${T.shadowMd}}
  .t-country-banner-inner{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:24px}
  .t-country-banner-label{font-size:11px;font-weight:700;color:${T.primary};letter-spacing:3px;text-transform:uppercase;margin-bottom:8px}
  .t-country-banner-title{font-size:clamp(18px,2.5vw,22px);font-weight:800;color:${T.text};margin-bottom:8px}
  .t-country-banner-desc{font-size:clamp(13px,1.5vw,14.5px);color:${T.textMuted};max-width:480px;line-height:1.7}
  .t-country-list{display:flex;gap:14px;flex-wrap:wrap}

  /* CTA */
  .t-cta-w{padding:0 clamp(16px,4vw,64px) 48px;background:${T.offWhite}}
  .t-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border:1px solid rgba(59,188,174,0.28);border-radius:20px;padding:clamp(24px,4vw,52px) clamp(24px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;position:relative;overflow:hidden}
  .t-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .t-cta-tag{display:inline-block;padding:4px 12px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:11px;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px;font-weight:700}
  .t-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.2rem,2.5vw,2rem);font-weight:900;color:${T.white};letter-spacing:-.025em;line-height:1.15}
  .t-cta-p{font-size:14px;color:${T.textMuted};margin-top:8px;line-height:1.6}
  .t-cta-r{display:flex;flex-direction:column;align-items:flex-end;gap:10px}
  .t-cta-btn{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn:hover{background:${T.gradGoldReverse};transform:translateY(-2px);box-shadow:${T.shadowGold}}
  .t-cta-note{font-size:11px;color:${T.textMuted};font-family:'JetBrains Mono',monospace}

  /* FOOTER */
  .t-footer{border-top:1px solid ${T.border};background:${T.white};padding:28px clamp(16px,4vw,64px)}
  .t-fi{max-width:1320px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px}
  .t-f-copy{font-size:12px;color:${T.textMuted};letter-spacing:.01em}

  /* BACK TO TOP */
  .t-btt{position:fixed;bottom:26px;right:26px;z-index:200;width:38px;height:38px;border-radius:9px;background:${T.gradGold};border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;color:${T.navyDeep};transition:all .18s;box-shadow:${T.shadowGold}}
  .t-btt:hover{transform:translateY(-2px);box-shadow:${T.shadowLg}}

  /* SKELETON */
  @keyframes t-pulse{0%,100%{opacity:.3}50%{opacity:.65}}
  .t-skel{background:${T.border};border-radius:6px;animation:t-pulse 1.4s ease-in-out infinite}
  .t-skel-member{background:${T.white};border-radius:22px;border:1px solid ${T.border};padding:28px}
  .t-skel-avatar{width:72px;height:72px;border-radius:50%;margin-bottom:16px}
  .t-skel-name{width:60%;height:24px;margin-bottom:12px}
  .t-skel-institution{width:80%;height:16px;margin-bottom:16px}
  .t-skel-field{width:40%;height:20px}
  .t-skel-bio{width:100%;height:60px;margin-top:16px}

  /* STATES */
  .t-sw{max-width:1320px;margin:0 auto;display:flex;flex-direction:column;align-items:center;text-align:center;padding:72px 24px;gap:12px}
  .t-s-icon{width:52px;height:52px;border-radius:13px;background:${T.softTeal};border:1px solid ${T.border};display:flex;align-items:center;justify-content:center;font-size:22px;color:${T.primary};margin-bottom:4px}
  .t-s-title{font-family:'Playfair Display',serif;font-size:20px;font-weight:800;color:${T.navy}}
  .t-s-sub{font-size:14px;color:${T.textMuted};max-width:300px;line-height:1.6}
  .t-s-btn{margin-top:4px;padding:9px 20px;border-radius:8px;border:1px solid ${T.border};background:${T.white};color:${T.text};font-family:'Inter',sans-serif;font-size:13px;font-weight:500;cursor:pointer;transition:all .15s}
  .t-s-btn:hover{border-color:${T.primary};color:${T.primary}}

  /* ─── RESPONSIVE OVERRIDES ─── */
  @media (max-width: 1024px) {
    .t-hero-i { flex-direction: column; align-items: center; text-align: center; }
    .t-hero-p { max-width: 100%; margin: 0 auto; }
    .t-stats { justify-content: center; }
    .t-stats .t-st { min-width: 120px; padding: 14px 20px; }
  }
  @media (max-width: 768px) {
    .t-members-grid { grid-template-columns: 1fr !important; gap: 20px !important; }
    .t-member-card { padding: 0; }
    .t-member-header { flex-direction: column; align-items: center; text-align: center; gap: 16px; padding: 24px 20px 0; }
    .t-member-avatar { width: 64px; height: 64px; font-size: 18px; }
    .t-member-name { font-size: 17px; justify-content: center; }
    .t-member-institution { font-size: 13px; text-align: center; }
    .t-member-field { font-size: 11px; }
    .t-member-divider { margin: 16px 20px; }
    .t-member-bio { padding: 0 20px 20px; font-size: 14px; text-align: center; }
    .t-country-banner-inner { flex-direction: column; align-items: flex-start; text-align: left; }
    .t-country-banner-title { font-size: 20px; }
    .t-country-banner-desc { max-width: 100%; }
    .t-country-list { width: 100%; justify-content: center; }
    .t-cta { flex-direction: column; text-align: center; }
    .t-cta-r { width: 100%; align-items: center; }
    .t-cta-btn { width: 100%; justify-content: center; }
    .t-hero-h { font-size: 2rem; }
  }
  @media (max-width: 480px) {
    .t-hero { padding: 24px 16px 20px; }
    .t-hero-h { font-size: 1.6rem; }
    .t-hero-lbl { font-size: 10px; padding: 4px 12px; }
    .t-stats { flex-direction: column; align-items: stretch; gap: 12px; }
    .t-st { min-width: auto; padding: 12px 16px; }
    .t-st-n { font-size: 28px; }
    .t-member-header { padding: 20px 16px 0; }
    .t-member-avatar { width: 56px; height: 56px; font-size: 16px; }
    .t-member-name { font-size: 16px; }
    .t-member-bio { font-size: 13px; padding: 0 16px 16px; }
    .t-country-banner { padding: 20px 16px; }
    .t-country-banner-title { font-size: 18px; }
    .t-country-list { gap: 10px; }
    .t-country-list > div { padding: 8px 12px; font-size: 12px; }
    .t-cta { padding: 24px 20px; }
    .t-cta-h { font-size: 1.2rem; }
    .t-cta-p { font-size: 0.9rem; }
    .t-cta-btn { font-size: 13px; padding: 12px 20px; }
    .t-footer { padding: 20px 16px; }
    .t-fi { flex-direction: column; text-align: center; gap: 8px; }
    .t-skel-member { padding: 20px; }
    .t-skel-avatar { width: 56px; height: 56px; }
  }
`;

const Committee = () => {
  const { eventId } = useSummit();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBtt, setShowBtt] = useState(false);

  console.log("Committee component - eventId from context:", eventId);

  useEffect(() => {
    if (eventId) {
      fetchMembers();
    } else {
      setError("No event selected");
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    const fn = () => setShowBtt(window.scrollY > 500);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const fetchMembers = async () => {
    if (!eventId) {
      console.error("Cannot fetch members: eventId is missing");
      setError("Event ID is required");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log(`Fetching committee members for event: ${eventId}`);
      
      const { data } = await axios.get(`${API_BASE}/api/committee/event/${eventId}`);
      console.log("Fetched members:", data);
      
      if (data.success && data.data) {
        const activeMembers = data.data.filter(m => m.isActive === true);
        setMembers(activeMembers);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching committee members:', err);
      setError(err.response?.data?.message || 'Failed to load committee members');
    } finally {
      setLoading(false);
    }
  };

  // Get unique countries for banner
  const countriesList = useMemo(() => {
    const countryMap = {};
    members.forEach(m => {
      if (!countryMap[m.countryCode]) {
        countryMap[m.countryCode] = { flag: m.countryCode, country: m.country, color: m.color || T.primary };
      }
    });
    return Object.values(countryMap);
  }, [members]);

  if (loading) {
    return (
      <div style={{ fontFamily: "'Inter', sans-serif", background: T.offWhite, minHeight: '100vh' }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl">
                <RiTeamLine size={13} />
                Scientific Leadership
              </div>
              <h1 className="t-hero-h">Organizing <em>Committee</em></h1>
              <p className="t-hero-p">Loading committee members...</p>
            </div>
          </div>
        </div>
        <div className="t-members-wrapper">
          <div className="t-members-container">
            <div className="t-members-grid">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="t-skel-member">
                  <div className="t-skel t-skel-avatar" />
                  <div className="t-skel t-skel-name" />
                  <div className="t-skel t-skel-institution" />
                  <div className="t-skel t-skel-field" />
                  <div className="t-skel t-skel-bio" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontFamily: "'Inter', sans-serif", background: T.offWhite, minHeight: '100vh' }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl">
                <RiTeamLine size={13} />
                Scientific Leadership
              </div>
              <h1 className="t-hero-h">Organizing <em>Committee</em></h1>
              <p className="t-hero-p">Global leaders shaping the future of academic research.</p>
            </div>
          </div>
        </div>
        <div className="t-sw">
          <div className="t-s-icon"><RiRefreshLine /></div>
          <p className="t-s-title">Failed to load committee members</p>
          <p className="t-s-sub">{error}</p>
          <button className="t-s-btn" onClick={fetchMembers}>Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: T.offWhite, minHeight: '100vh' }}>
      <style>{css}</style>

      {/* Hero Section */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div>
            <div className="t-hero-lbl">
              <RiTeamLine size={13} />
              Scientific Leadership
            </div>
            <h1 className="t-hero-h">
              Organizing <em>Committee</em>
            </h1>
            <p className="t-hero-p">
              Our conference is curated by a distinguished panel of experts from around the world. 
              The Organizing Committee brings together leading researchers, academicians, and 
              industry professionals to ensure the highest scientific quality.
            </p>
          </div>

          {/* Stats Cards */}
          <div className="t-stats">
            <div className="t-st">
              <span className="t-st-n">{members.length}</span>
              <span className="t-st-l">Committee Members</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">{countriesList.length}</span>
              <span className="t-st-l">Countries</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">20+</span>
              <span className="t-st-l">Years Experience</span>
            </div>
          </div>
        </div>
      </section>

      {/* Members Grid */}
      <div className="t-members-wrapper">
        <div className="t-members-container">
          {members.length === 0 ? (
            <div className="t-sw">
              <div className="t-s-icon"><RiTeamLine /></div>
              <p className="t-s-title">No Committee Members Yet</p>
              <p className="t-s-sub">Committee members will appear here once added.</p>
            </div>
          ) : (
            <>
              <div className="t-members-grid">
                {members.map((member, idx) => (
                  <motion.div
                    key={member._id || idx}
                    className="t-member-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                    style={{ '--member-color': member.color || T.primary }}
                  >
                    <div className="t-member-header">
                      <div 
                        className="t-member-avatar" 
                        style={{ 
                          background: `linear-gradient(135deg, ${member.color || T.primary}33, ${member.color || T.primary}66)`,
                          border: `2px solid ${member.color || T.primary}55`
                        }}
                      >
                        {member.initials}
                      </div>
                      <div className="t-member-info">
                        <div className="t-member-name">
                          {member.name}
                          <span className="t-member-flag">{member.countryCode}</span>
                          {member.isChair && <span style={{ fontSize: 10, background: T.accentGold, color: T.navyDeep, padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Chair</span>}
                          {member.isCoChair && <span style={{ fontSize: 10, background: T.primary, color: T.white, padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Co-Chair</span>}
                        </div>
                        <p className="t-member-institution" style={{ color: member.color || T.primary }}>{member.institution}</p>
                        <span className="t-member-field" style={{ background: `${member.color || T.primary}10`, border: `1px solid ${member.color || T.primary}30`, color: member.color || T.primary }}>
                          {member.field}
                        </span>
                      </div>
                    </div>
                    <div className="t-member-divider" />
                    <p className="t-member-bio">{member.bio}</p>
                    <div className="t-member-footer" style={{ background: `linear-gradient(90deg, ${member.color || T.primary}, transparent)` }} />
                  </motion.div>
                ))}
              </div>

              {/* World Coverage Banner */}
              {countriesList.length > 0 && (
                <motion.div
                  className="t-country-banner"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className="t-country-banner-inner">
                    <div>
                      <div className="t-country-banner-label">Global Representation</div>
                      <h3 className="t-country-banner-title">Members from {countriesList.length} Countries</h3>
                      <p className="t-country-banner-desc">
                        Our committee spans multiple continents, bringing diverse perspectives and
                        world-class expertise in academic and research fields.
                      </p>
                    </div>
                    <div className="t-country-list">
                      {countriesList.map((c, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.offWhite, border: `1px solid ${c.color}30`, borderRadius: 13, padding: '12px 18px', boxShadow: T.shadow }}>
                          <span style={{ fontSize: 22 }}>{c.flag}</span>
                          <span style={{ fontSize: 13.5, fontWeight: 600, color: T.text }}>{c.country}</span>
                          <div style={{ width: 7, height: 7, borderRadius: '50%', background: c.color, marginLeft: 4 }} />
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* CTA */}
              <div className="t-cta-w">
                <motion.div
                  className="t-cta"
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  viewport={{ once: true }}
                >
                  <div>
                    <div className="t-cta-tag">Applications Open</div>
                    <h2 className="t-cta-h">Interested in Joining<br />the Committee?</h2>
                    <p className="t-cta-p">We welcome eminent researchers and professors to join our scientific and organizing committee.</p>
                  </div>
                  <div className="t-cta-r">
                    <Link to={`/event/${window.location.pathname.split('/')[2]}/contact`} className="t-cta-btn">
                      Contact Us to Join
                      <RiArrowRightLine size={15} />
                    </Link>
                    <div className="t-cta-note">Open for applications</div>
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Back to Top Button */}
      <AnimatePresence>
        {showBtt && (
          <motion.button
            className="t-btt"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Back to top"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.16 }}
          >
            <RiArrowUpLine size={15} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Committee;